/* eslint-disable no-null/no-null */
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Subscription } from 'rxjs';

import { Accessibility, CimaToken, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { Account, User } from 'store/user/models';

import { InternationalRate } from 'store/international-rates/models/international-rates';
import { CountryStatus } from '../../international/constants';
import { StateService } from '@uirouter/core';
import { GtpPassCountry } from 'my-account/constants';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';

@Component({
    selector: 'xm-travel-rate-details',
    styleUrls: ['./travel-rate-details.scss'],
    templateUrl: './travel-rate-details.html',
    standalone: false
})
export class XmTravelRateDetails implements OnInit, OnChanges, OnDestroy {
    @Input() public rateDetails: InternationalRate;
    @Input() public pageData: CmsInternationalTravelRates;
    @Input() public showGtpLink: boolean;
    @Input() public isInternationalSearch: boolean;
    @Output() public gtpLauncher: EventEmitter<void> = new EventEmitter<void>();

    public showInitialState: boolean = true;
    public showGtpEligibleCard: boolean;
    public showGtpInEligibleCard: boolean;
    public showCoverageNotAvailable: boolean;
    public noCoverageDescription: string;
    public gtpEligibleText: string;
    public gtpIneligibleText: string;
    public payAsGoCardResultState: string;
    public payAsGoRateDetails: CmsInternationalDetailsDescription[];
    public eligibleIcon: MediaImageOptions;
    public inEligibleIcon: MediaImageOptions;
    public isMexCan: boolean = false;
    public isUserAuthenticated: boolean = false;
    public canMexCta: string;
    public enableCbmGeminiTR4: boolean = false;
    public isTmoAccount: boolean = false;

    private currencyPipe: CurrencyPipe;
    private accessibility: Accessibility;
    private cimaToken: CimaToken;
    private state: StateService;
    private config: IXMOptions;
    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];

    constructor(accessibility: Accessibility, currencyPipe: CurrencyPipe, cimaToken: CimaToken, state: StateService, @Inject(CONFIG_TOKEN) config: IXMOptions, xmStore: XmStore) {
        Object.assign(this, { accessibility, currencyPipe, cimaToken, state, config, xmStore });
    }

    public ngOnInit(): void {
        if (this.cimaToken.isLoggedIn) {
            this.isUserAuthenticated = true;
            this.enableCbmGeminiTR4 = this.config.ENABLE_CBM_INTERNATIONAL_RETE_LINK && this.cimaToken.isBusinessUser;

            if (this.enableCbmGeminiTR4) {
                this.subscriptions.push(
                    XmStoreUtil.subscribe(
                        this.xmStore.peekChild<Account, User>('account', Account, User),
                        (account: Account) => {
                            this.isTmoAccount = account.isNoLevel;
                        }
                    )
                );
            }
        }

        this.eligibleIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.eligibleIcon)
        };
        this.inEligibleIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.pageData.inEligibleIcon)
        };

    }

    public ngOnChanges(): void {
        this.resetFlags();

        if (!this.rateDetails) {
            this.showInitialState = true;
        } else {
            this.manageGtp();
            this.payAsGoCardResultState = this.replaceData(this.pageData.payAsGoCardResultState);

            if (this.rateDetails.status === CountryStatus.BLACKLISTED) {
                this.showCoverageNotAvailable = true;
                this.noCoverageDescription = this.replaceData(this.pageData.noCoverageDescription);
            } else if (this.rateDetails.gtpAvailable) {
                this.showGtpEligibleCard = true;
                this.gtpEligibleText = this.replaceData(this.pageData.gtpEligibleText);
                this.accessibility.announceReaderText(this.pageData.callRatesReaderText.replace(/\[COUNTRY\]/g, this.rateDetails.displayName));
            } else {
                this.showGtpInEligibleCard = true;
                if (this.rateDetails.rates.roamingMessages === null && this.rateDetails.rates.roamingMinutes === null) {
                    this.gtpIneligibleText = this.replaceData(this.pageData.gtpUnavailableText);
                } else {
                    this.gtpIneligibleText = this.replaceData(this.isMexCan ? this.pageData.canMexIneligibleText : this.pageData.gtpIneligibleText);
                }
                this.accessibility.announceReaderText(this.pageData.callRatesReaderText.replace(/\[COUNTRY\]/g, this.rateDetails.displayName));
                this.payAsGoCardResultState = this.replaceData(this.isMexCan ? this.pageData.payAsGoCardResultIneligibleStateCanMex : this.pageData.payAsGoCardResultIneligibleState);
            }

            this.payAsGoRateDetails = this.pageData.payAsGoRateDetails.map((rateItem: CmsInternationalDetailsDescription) =>
                ({
                    label: rateItem.label,
                    subtext: rateItem.subtext,
                    price: this.replaceData(rateItem.price)
                })
            );
        }
    }

    public resetFlags(): void {
        this.showInitialState = false;
        this.showGtpEligibleCard = false;
        this.showGtpInEligibleCard = false;
        this.showCoverageNotAvailable = false;
        this.isMexCan = false;
    }

    public replaceData(inputString: string): string {
        return inputString.replace('[COUNTRY]', this.rateDetails.displayName)
            .replace('[PRICE]', this.rateDetails.gtpPrice? this.rateDetails.gtpPrice : 'Unavailable')
            .replace('[MINUTES]', (this.rateDetails.rates.roamingMinutes || this.rateDetails.rates.roamingMinutes === 0) ? this.currencyPipe.transform(this.rateDetails.rates.roamingMinutes) : 'Unavailable')
            .replace('[MESSAGES]', (this.rateDetails.rates.roamingMessages || this.rateDetails.rates.roamingMessages === 0) ? this.currencyPipe.transform(this.rateDetails.rates.roamingMessages) : 'Unavailable')
            .replace('[DATA]', (this.rateDetails.rates.roamingData || this.rateDetails.rates.roamingData === 0) ? this.currencyPipe.transform(this.rateDetails.rates.roamingData) : 'Unavailable');
    }

    public manageGtp(): void {
        this.isMexCan = this.rateDetails.displayName.toUpperCase() === GtpPassCountry.MEXICO || this.rateDetails.displayName.toUpperCase() === GtpPassCountry.CANADA;
        this.canMexCta = this.isUserAuthenticated ? this.pageData.canMexManageCta : this.pageData.canMexLearnMoreCta;
    }

    public launchGtpFlow(): void {
        this.gtpLauncher.emit();
    }

    public onCanMexCtaClick(): void {
        this.state.go('account.activity.landing', { tab: 'mexcan' });
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }
}
